import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Button, TextArea, LoadIndicator } from 'devextreme-react';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { formatMessage } from 'devextreme/localization';

import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';

import ComplexTransformationGraph from './ComplexTransformationGraph';
import GenerateAITransformationResult from '../../classes/api/result/pathsynchromodule/GenerateAITransformationResult';
import { parseSteps, pick, readTestData, StoredTestData, withLabels, withTestData } from './complexTransformationSteps';

import './TransformationPopup.css';

interface ComplexTransformationPopupProps {
    visible: boolean;
    title: string;
    currentParameters: string;
    transformationsCapabilities: TransformationCapabilitiesDto[];
    // Readable name for a source field id used as a step input (falls back to the raw id).
    getFieldLabel?: (id: string) => string;
    // Calls the AI endpoint; resolves to the JSON settings, or rejects with a user-displayable message.
    onGenerate(prompt: string): Promise<GenerateAITransformationResult>;
    onApply(parameters: string): void;
    onCancel(): void;
}

// The user prompt is persisted inside the settings JSON itself (extra "Prompt" property, ignored by the
// back-end deserializer) so the popup can be reopened and the prompt edited.
export function readPrompt(parameters: string): string {
    try {
        const value = pick(JSON.parse(parameters), 'Prompt');
        return typeof value === 'string' ? value : "";
    } catch {
        return "";
    }
}

export function withPrompt(parameters: string, prompt: string): string {
    try {
        const parsed = JSON.parse(parameters);
        Object.keys(parsed).filter((k) => k.toLowerCase() === 'prompt').forEach((k) => delete parsed[k]);
        return JSON.stringify({ ...parsed, Prompt: prompt });
    } catch {
        return parameters;
    }
}

// An empty value is shown as a muted "empty" marker so it is not confused with an empty string.
const renderTestCell = (cell: { value: string | null | undefined }): JSX.Element => (
    cell.value === null || cell.value === undefined
        ? <span className="complex-transformation-test-empty">{formatMessage("ComplexTransformationPopup_TestEmpty")}</span>
        : <span>{cell.value}</span>
);

const ComplexTransformationPopup: React.FC<ComplexTransformationPopupProps> = ({
    visible, title, currentParameters, transformationsCapabilities, getFieldLabel, onGenerate, onApply, onCancel
}) => {

    const [prompt, setPrompt] = useState<string>("");
    const [result, setResult] = useState<string>(currentParameters);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    // JSON of the last API response, kept even when its test failed, so Debug can show it.
    const [lastResponse, setLastResponse] = useState<string>("");
    const [debugVisible, setDebugVisible] = useState<boolean>(false);
    // Fake-data execution of the generated settings; persisted inside the settings JSON, so it is read back on opening.
    const [testData, setTestData] = useState<StoredTestData | null>(() => readTestData(currentParameters));

    useEffect(() => {
        if (visible) {
            setPrompt(readPrompt(currentParameters));
            setResult(currentParameters);
            setError("");
            setLastResponse("");
            setTestData(readTestData(currentParameters));
            setDebugVisible(false);
            setLoading(false);
        }
    }, [visible, currentParameters]);

    const resolveFieldLabel = useCallback((id: string) => getFieldLabel?.(id) ?? id, [getFieldLabel]);
    const targetLabel = formatMessage("ComplexTransformationPopup_Result");

    // Pretty-printed settings, read-only; falls back to the raw text when it is not valid JSON.
    // The last API response wins when there is one, otherwise the current settings.
    const debugSource = lastResponse || result;
    const formattedJson = useMemo(() => {
        if (!debugSource) {
            return "";
        }
        try {
            return JSON.stringify(JSON.parse(debugSource), null, 2);
        } catch {
            return debugSource;
        }
    }, [debugSource]);

    const rows = useMemo(() => parseSteps(result, transformationsCapabilities), [result, transformationsCapabilities]);

    // One flat row per combination, one property per input field (f<fieldId>), as the grid columns expect.
    const testGridData = useMemo(() => (testData?.Rows ?? []).map((row, index) => {
        const flat: Record<string, string | number | null> = { __index: index, __output: row.Output };
        testData?.InputColumns.forEach((c) => { flat[`f${c.FieldId}`] = row.Inputs[String(c.FieldId)] ?? null; });
        return flat;
    }), [testData]);

    const handleGenerate = async () => {
        setLoading(true);
        setError("");
        setTestData(null);
        try {
            const generated = await onGenerate(prompt.trim());
            if (generated.Settings) {
                setLastResponse(generated.Settings);
            }
            if (!generated.Settings) {
                setError(formatMessage("ComplexTransformationPopup_EmptyResult"));
            } else if (generated.Error) {
                setError(generated.Error);
            } else {
                // The labels travel with the settings (like the prompt) so the graph can still name the steps
                // when the popup is reopened later, without calling the API again.
                setResult(withTestData(withLabels(generated.Settings, generated.Labels), generated));
                setTestData(generated);
            }
        } catch (e: any) {
            setError(e?.message ?? formatMessage("ComplexTransformationPopup_GenerationFailed"));
        } finally {
            setLoading(false);
        }
    };

    const canApply = !loading && !!result && rows !== null && rows.length > 0;

    const renderResult = (): JSX.Element => {
        if (loading) {
            return <div className="complex-transformation-placeholder"><LoadIndicator visible={true} /></div>;
        }
        if (error) {
            return <div className="complex-transformation-placeholder complex-transformation-error">{error}</div>;
        }
        if (rows === null) {
            return <div className="complex-transformation-placeholder complex-transformation-error">{formatMessage("ComplexTransformationPopup_InvalidResult")}</div>;
        }
        if (rows.length === 0) {
            return <div className="complex-transformation-placeholder">{formatMessage("ComplexTransformationPopup_NoResult")}</div>;
        }
        return <ComplexTransformationGraph steps={rows} getFieldLabel={resolveFieldLabel} targetLabel={targetLabel} />;
    };

    const renderContent = (): JSX.Element => (
        <div className="complex-transformation-popup-content">
            <div className="transformation-popup-field">
                <label>{formatMessage("ComplexTransformationPopup_Prompt")}</label>
                <TextArea
                    value={prompt}
                    height={110}
                    stylingMode="outlined"
                    placeholder={formatMessage("ComplexTransformationPopup_PromptPlaceholder")}
                    valueChangeEvent="input"
                    onValueChanged={(e) => setPrompt(e.value ?? "")}
                />
                <div className="complex-transformation-generate">
                    <Button
                        text={formatMessage("ComplexTransformationPopup_Generate")}
                        icon="runner"
                        type="default"
                        stylingMode="contained"
                        disabled={loading || prompt.trim().length === 0}
                        onClick={handleGenerate}
                    />
                </div>
            </div>

            <div className="complex-transformation-result-row">
                <div className="transformation-popup-field complex-transformation-result">
                    <label>{formatMessage("ComplexTransformationPopup_Result")}</label>
                    <div className="complex-transformation-result-body">{renderResult()}</div>
                </div>
            </div>

            {testData !== null && testData.Rows.length > 0 && (
                <div className="transformation-popup-field complex-transformation-test">
                    <label>{formatMessage("ComplexTransformationPopup_TestData")}</label>
                    <DataGrid
                        dataSource={testGridData}
                        keyExpr="__index"
                        height={Math.min(testData.Rows.length * 27 + 34, 190)}
                        showBorders={true}
                        showRowLines={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        hoverStateEnabled={true}
                        rowAlternationEnabled={false}
                        wordWrapEnabled={true}
                        sorting={{ mode: 'none' }}
                        paging={{ enabled: false }}
                        scrolling={{ mode: 'standard' }}
                    >
                        {testData.InputColumns.map((c) => (
                            <Column key={c.FieldId} dataField={`f${c.FieldId}`} caption={c.Name} cellRender={renderTestCell} />
                        ))}
                        <Column dataField="__output" caption={testData.OutputColumnName} cssClass="complex-transformation-test-output" cellRender={renderTestCell} />
                    </DataGrid>
                </div>
            )}

            <div className="transformation-popup-actions">
                <Button
                    text={formatMessage("ComplexTransformationPopup_Debug")}
                    icon="search"
                    onClick={() => setDebugVisible(true)}
                    disabled={!debugSource}
                    stylingMode="outlined"
                    type="normal"
                />
                <Button
                    text={formatMessage("TransformationPopup_Cancel")}
                    onClick={onCancel}
                    stylingMode="outlined"
                    type="normal"
                />
                <Button
                    text={formatMessage("TransformationPopup_Apply")}
                    onClick={() => onApply(withPrompt(result, prompt))}
                    disabled={!canApply}
                    stylingMode="contained"
                    type="default"
                />
            </div>
        </div>
    );

    const renderDebugContent = (): JSX.Element => (
        <TextArea
            value={formattedJson}
            readOnly={true}
            height="100%"
            stylingMode="outlined"
            spellcheck={false}
            elementAttr={{ class: 'complex-transformation-debug-body' }}
        />
    );

    return (
        <>
        <Popup
            visible={debugVisible}
            onHiding={() => setDebugVisible(false)}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title={formatMessage("ComplexTransformationPopup_Json")}
            width={700}
            height={560}
            maxWidth="95vw"
            maxHeight="92vh"
            contentRender={renderDebugContent}
        />
        <Popup
            visible={visible}
            onHiding={onCancel}
            dragEnabled={false}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title={title}
            width={1100}
            height={780}
            maxWidth="95vw"
            maxHeight="92vh"
            deferRendering={false}
            animation={{
                show: { type: 'fade', from: 0, to: 1, duration: 90 },
                hide: { type: 'fade', from: 1, to: 0, duration: 60 },
            }}
            contentRender={renderContent}
        />
        </>
    );
};

export default ComplexTransformationPopup;
